<template>
    <div class="flex h-full py-1 pb-2">
        <div class="border-l-4 border-primary border-opacity-50 ml-2 mr-4 h-full"/>
        <div class="grow py-4 pr-2 h-full overflow-auto">
            <div class="text-xl mt-4 mb-2">
                Bankkontoer
            </div>
            <table class="w-full">
                <thead>
                    <tr>
                        <th class="w-72">Bankkonto</th>
                        <th class="w-28 text-right pr-64">Virksomhet</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="account in accounts" :key="account.uuid" class="max-h-8">
                        <td>
                            <div>{{ formatBankAccountNo(account.bban) + ' - ' + account.name }}</div>
                        </td>
                        <td>
                            <div class="flex justify-end pr-5">
                                <multiselect
                                    :options="onboardedOrgs"
                                    class="min-w-48 max-w-80 text-xs"
                                    placeholder="Velg virksomhet"
                                    label="organizationName"
                                    track-by="organizationId"
                                    select-label=""
                                    size="small"
                                    :show-labels="true"
                                    :disabled="consent.status !== 'active'"
                                    :model-value="getSelected(account)"
                                    @update:model-value="(tenant: Tenant | undefined) => assignAccountHandler(tenant, account)"
                                    @remove="(tenant: Tenant | undefined) => unassignAccountHandler(tenant, account)"
                                >
                                    <template #singleLabel="{ option }">
                                        {{ option.organizationName || 'Velg organisasjon' }}
                                    </template>
                                    <template #noResult="{}">Ingen treff</template>
                                </multiselect>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from 'vue'
import {Account, AssignAccountCommand, UnassignAccountCommand} from '@/bank/account/account.types'
import {Consent} from '@/bank/consent/consent.types'
import {useConsent} from '@/bank/consent/consent.service'
import {formatBankAccountNo} from '@/bank/bank-account.service'
import Multiselect from 'vue-multiselect'
import {myOrgs} from '@/tenants/org.service'
import {Tenant} from '@/tenants/Tenant'
import _ from 'lodash'
import {useAccount} from '@/bank/account/account.service'
import {Onboarding, OnboardingStatus} from '@/bank/onboarding/onboarding.types'
import {useOnboarding} from '@/bank/onboarding/onboarding.service'

const {assignAccount, unassignAccount} = useAccount()
const {getUserOnboardings} = useOnboarding()
const {getAccountsByConsent} = useConsent()

const props = defineProps({
    consent: {
        type: Object as () => Consent,
        required: true
    }
})

const accounts = ref<Account[]>([])
const onboardings = ref<Onboarding[]>([])
const onboardedTenantIds = computed(() => onboardings.value.filter(o => o.onboardingStatus === OnboardingStatus.Accepted).map(o => o.tenantId))
const onboardedOrgs = computed<Tenant[]>(() => _.sortBy(myOrgs.value.filter(o => onboardedTenantIds.value.includes(o.organizationId)), 'organizationName'))

const getSelected = (account: Account): Tenant | undefined => {
    return myOrgs.value.find(o => o.organizationId === account.tenantId) ?? undefined
}

const assignAccountHandler = async (tenant: Tenant | undefined, account: Account) => {
    // Get selected can be undefined
    if (!tenant) return
    await assignAccount({tenantId: tenant.organizationId, accountUuid: account.uuid} as AssignAccountCommand)
    await refresh()
}

const unassignAccountHandler = async (tenant: Tenant | undefined, account: Account) => {
    if (!tenant) return
    await unassignAccount({tenantId: tenant.organizationId, accountUuid: account.uuid} as UnassignAccountCommand)
    await refresh()
}

const refresh = async () => {
    accounts.value = await getAccountsByConsent(props.consent.uuid)
    onboardings.value = await getUserOnboardings()
}

onMounted(refresh)
</script>

<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

table {
    tr {
        @apply border-b dark:border-gray-400/30;
    }
}
</style>